import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { Iframe } from '../../custom-ui';
import type { IframeProps } from '../../custom-ui/iframe/types';
import {
	type ModalIframeRendererProps,
	type CustomUIRendererProps,
	type InternalModalState,
	type EmbeddedUIKitModalContext,
} from './types';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { cssMap } from '@atlaskit/css';
import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ConfluenceExtension, JiraExtension } from '@atlassian/forge-ui-types';
import { ModalContentWrapper, resolveModalDimensions } from './custom-ui-modal-utils';
import {
	ModalCustomUIRendererHeader,
	type ModalHeaderProps,
} from './modal/ModalCustomUIRendererHeader';
import { FullscreenModal } from './modal/FullscreenModal';
import { ForgeModalDeprecationMessage } from '../ForgeModalDeprecationMessage';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../../utils/getExtensionType';
import { resolveDynamicResource } from '../../utils/resolveDynamicResource';
import { EmbeddedUIKitModal } from './EmbeddedUIKitModal';
import { FULLSCREEN_SUPPORTED_EXTENSION_TYPES } from './modal/types';
import { isNamedSize } from '../../custom-ui/bridge/types';
import { ForgeAppErrorMessage } from '../errorMessages';
import { shouldRenderForgeAppErrorMessage } from '../errorMessages/errorTypes';

const styles = cssMap({
	modalDeprecationMessage: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type Props = CustomUIRendererProps & {
	isInModal?: boolean;
	// These are internal props, not exposed to the product
	onModalCustomUIRendererClose?: () => Promise<void>;
	setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CustomUIRenderer = ({
	accountId,
	bridge,
	contextIds,
	contextChangeListeners,
	coreData,
	customBridgeMethods,
	customBridge,
	customBridgeContext,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	height,
	width,
	isIframeHidden,
	isIframeResizable,
	iframeRef,
	isInModal,
	loadingComponent,
	locale,
	localId,
	modalStyles,
	modalProvider,
	onIframeLoad,
	onModalCustomUIRendererClose,
	product,
	timezone,
	getModalWidth,
	setIsLoading,
	isEmbeddedMacro,
	embeddedMacroParentType,
	embeddedParentLocalId,
}: Props): React.JSX.Element => {
	const [error, setError] = useState<string | null>(null);
	// modalOptions is null when no modal is open is non null when opened
	// Uses InternalModalState to allow extension property from FAUS forwarding
	const [modalOptions, setModalOptions] = useState<InternalModalState | null>(null);
	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	const [embeddedUIKitModalContext, setEmbeddedUIKitModalContext] =
		useState<EmbeddedUIKitModalContext | null>(null);

	const onCloseFromModalClass = modalOptions?.onClose;
	const embeddedMacroModalProps = modalOptions?.isEmbeddedMacroModal
		? {
				extensionPayload: modalOptions?.extensionPayload,
				// Extension view data in macro module is only reserved for bodied macro content.
				// Since we don't support embedded bodied macros, extensionViewData will always be undefined.
				extensionViewData: undefined,
				coreData: {
					...coreData,
					localId: modalOptions?.embeddedMacroLocalId || '',
				},
			}
		: {};

	const iframeProps = {
		accountId,
		contextIds,
		contextChangeListeners,
		components: getThreeLOPrompt,
		coreData,
		customBridgeMethods,
		customBridge,
		customBridgeContext,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		isHidden: isIframeHidden,
		isResizable: isIframeResizable,
		locale,
		timezone,
		loadingComponent,
		isEmbeddedMacro,
		embeddedMacroParentType,
		embeddedParentLocalId,
		...(fg('platform_faus_navigate_to_product') ? {} : embeddedMacroModalProps),
	};
	const modalIframeProps = {
		...iframeProps,
		...embeddedMacroModalProps,
	};

	const isBodiedMacro = extension.properties?.layout === 'bodied';

	const extensionInModal = useMemo(() => {
		// Use the embedded macro's extension if provided (for modals opened from embedded macros)
		const baseExtension = modalOptions?.extension || extension;
		// `openModal`'s resource is optional; only override when one is explicitly provided. When it is
		// omitted the modal defaults to the current resource, so we inherit it (and its entry) untouched.
		let resourceOverride: { resolvedResourceEntry?: string; resource?: string } = {};
		if (modalOptions?.resource) {
			resourceOverride = resolveDynamicResource(modalOptions.resource);
		}
		return {
			...baseExtension,
			// Pass a type that is not included in the dimensions presets so that it falls back to default
			type: `${baseExtension.type}::dynamic-modal`,
			properties: {
				...baseExtension.properties,
				...resourceOverride,
			},
		};
	}, [extension, modalOptions?.resource, modalOptions?.extension]);

	const onError = (error: string | undefined) => {
		// Under bridge v2 the runtime overlay owns error display, so don't take over the
		// surface here — setting error would unmount the iframe and the overlay with it.
		if (error && !fg('forge-ui-bridge-core-reset')) {
			setError(error);
		}
	};

	const extensionDataInModal = useMemo(() => {
		// Use the embedded macro's extensionData if provided (for modals opened from embedded macros)
		const baseExtensionData = modalOptions?.extensionData || extensionData;
		return {
			...baseExtensionData,
			...(modalOptions?.context ? { modal: modalOptions?.context } : {}),
			// This is to handle an incompatibility issue with Compass and JSM where they are incorrectly currently spreading the context object on the top level instead of in the `modal` object.
			// Remove once Compass apps have been updated https://softwareteams.atlassian.net/browse/COMPASS-22051 and a notice to app developers have been sent out
			...((product === 'compass' || extension.type.startsWith('jiraServiceManagement')) &&
			modalOptions?.context
				? { ...modalOptions?.context }
				: {}),
		};
	}, [extension.type, extensionData, modalOptions?.context, modalOptions?.extensionData, product]);

	const handleModalTearDown = useCallback(() => {
		setModalOptions(null);
	}, [setModalOptions]);

	const isReducedMotion = useIsReducedMotion();

	const handleModalClose = useCallback(() => {
		onCloseFromModalClass?.();
		// if reduced motion is enabled, we need to call handleModalTearDown here as it no longer calls `onCloseComplete` in `ModalCustomUIRenderer`
		if (isReducedMotion) {
			handleModalTearDown();
		}
	}, [isReducedMotion, onCloseFromModalClass, handleModalTearDown]);

	// Confluence specific feature
	// Needed to show Forge modals in live pages. dialogs won't be passed in if the page isn't live.
	useEffect(() => {
		if (product !== 'confluence') {
			return;
		}
		if (dialogs && modalOptions) {
			dialogs.showDialog(ModalCustomUIRenderer, {
				extension: extensionInModal,
				extensionData: extensionDataInModal,
				localId,
				onTearDown: handleModalTearDown,
				bridge,
				size: modalOptions.size,
				closeOnEscape: modalOptions.closeOnEscape,
				closeOnOverlayClick: modalOptions.closeOnOverlayClick,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOptions, dialogs]);

	// Only reachable without bridge-core-reset; under it `error` stays null and the runtime overlay
	// renders ForgeAppErrorMessage instead.
	// TODO: remove once forge-ui-bridge-core-reset is fully rolled out.
	if (error && shouldRenderForgeAppErrorMessage(error)) {
		return <ForgeAppErrorMessage extension={extension} error={error} />;
	}

	return (
		<>
			{isBodiedMacro &&
			embeddedUIKitModalContext?.forgeDoc &&
			embeddedUIKitModalContext?.extension &&
			fg('platform_forge_ui_uikit_modal_in_custom_ui_parent') ? (
				<EmbeddedUIKitModal
					accountId={accountId}
					bridge={bridge}
					coreData={coreData}
					customBridgeMethods={customBridgeMethods}
					forgeDoc={embeddedUIKitModalContext.forgeDoc}
					extension={embeddedUIKitModalContext.extension}
					getThreeLOPrompt={getThreeLOPrompt}
					loadingComponent={loadingComponent}
					product={product}
					isEmbeddedMacro={isEmbeddedMacro}
					embeddedParentLocalId={embeddedParentLocalId}
				/>
			) : null}
			<Iframe
				{...iframeProps}
				entryPoint={entryPoint}
				isResizable={isIframeResizable}
				customBridge={
					{
						...customBridge,
						...(isBodiedMacro && fg('forge-ui-bridge-core-reset')
							? {
									setEmbeddedMacroModalForgeDoc: (
										_ctx: unknown,
										payload: { embeddedUIKitModalContext: EmbeddedUIKitModalContext } | undefined,
									) => {
										setEmbeddedUIKitModalContext(payload?.embeddedUIKitModalContext ?? null);
									},
								}
							: {}),
					} as NonNullable<IframeProps['customBridge']>
				}
				bridge={{
					...bridge,
					openModal: (opts) => {
						setModalOptions(opts.data);
						return true;
					},
					...(onModalCustomUIRendererClose
						? {
								close: (payload: any) => {
									onModalCustomUIRendererClose()
										.then(() => {
											bridge?.close?.(payload);
										})
										.finally(() => {
											return true;
										});
								},
							}
						: {}),
				}}
				customBridgeMethods={{
					...customBridgeMethods,
					...(isBodiedMacro && fg('platform_forge_ui_uikit_modal_in_custom_ui_parent')
						? {
								setEmbeddedMacroModalForgeDoc: (payload: {
									embeddedUIKitModalContext: EmbeddedUIKitModalContext;
								}) => {
									setEmbeddedUIKitModalContext(payload?.embeddedUIKitModalContext);
								},
							}
						: {}),
				}}
				height={height}
				width={width}
				isInModal={isInModal}
				ref={iframeRef}
				onLoad={onIframeLoad}
				setIsLoading={setIsLoading}
				onError={onError}
			/>
			{modalOptions ? (
				<ModalCustomUIRenderer
					{...(fg('platform_faus_navigate_to_product') ? modalIframeProps : iframeProps)}
					product={product}
					getThreeLOPrompt={getThreeLOPrompt}
					dialogs={dialogs}
					extension={extensionInModal}
					extensionData={extensionDataInModal}
					localId={modalOptions?.embeddedMacroLocalId || localId}
					onTearDown={handleModalTearDown}
					bridge={{
						...bridge,
						onClose: (callback) => {
							setViewOnClose(() => callback.data);
							// stores the callback for when modal is closed
							return true;
						},
						close: (payload: any) => {
							onCloseFromModalClass?.(payload.data);
							return true;
						},
					}}
					modalOnClose={handleModalClose}
					size={modalOptions.size}
					title={modalOptions.title}
					icon={modalOptions.icon}
					closeOnEscape={modalOptions.closeOnEscape}
					closeOnOverlayClick={modalOptions.closeOnOverlayClick}
					modalStyles={modalStyles}
					modalProvider={modalProvider}
					getModalWidth={getModalWidth}
					isIframeResizable={isIframeResizable}
					viewOnClose={viewOnClose}
					customBridge={customBridge}
				/>
			) : null}
		</>
	);
};

export const ModalCustomUIRenderer = ({
	accountId,
	bridge,
	coreData,
	closeOnEscape,
	closeOnOverlayClick,
	consentMessage,
	contextChangeListeners,
	contextIds,
	customBridgeMethods,
	customBridge,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	isIframeHidden = false,
	isIframeResizable = false,
	loadingComponent,
	locale,
	localId,
	mentionProvider,
	modalExtension,
	modalStyles,
	modalProvider: ModalProvider,
	onConsentModalClose,
	onConsentSuccess,
	onError,
	onForgeDocUpdated,
	onIframeLoad,
	onTearDown,
	product,
	size,
	title,
	icon,
	timezone,
	getModalWidth,
	// onClose from `Modal`
	modalOnClose,
	// onClose from `view`
	viewOnClose,
}: ModalIframeRendererProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(true);

	// This is for preventing inline dialog apps from being closed by an esc key press when a
	// modal is open until https://product-fabric.atlassian.net/browse/DSP-3552 is resolved.
	useEffect(() => {
		if (
			product !== 'confluence' ||
			modalExtension?.type === 'macroConfig' ||
			(product === 'confluence' && modalExtension?.type === 'bylineItem')
		) {
			return;
		}
		function dismissEscape(e: any) {
			if (e.key === 'Escape') {
				e.stopPropagation();

				if (closeOnEscape) {
					modalOnClose?.();
					setIsOpen(false);
				}
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', dismissEscape, { capture: true });
		}

		return () =>
			document.removeEventListener('keydown', dismissEscape, {
				capture: true,
			});
	}, [isOpen, closeOnEscape, product, modalOnClose, modalExtension?.type]);

	const { trackExtensionLoaded, trackBridgeSucceed, trackBridgeFailed } =
		useForgeUiAnalyticsEvent();

	const extensionType = getExtensionType(extension?.properties);
	const environmentType = extension?.environmentType;
	const isMacroExtension = extension?.type === ConfluenceExtension.MACRO;
	// Only tracking fullscreen modals
	useEffect(() => {
		if (modalExtension?.modalWidth === 'fullscreen') {
			trackExtensionLoaded({
				forgeEnvironment: environmentType,
				renderType: 'CustomUI',
				extensionType: extensionType,
				viewportSize: 'fullscreen',
			});
		}
	}, [modalExtension?.modalWidth, environmentType, extensionType, trackExtensionLoaded]);

	const modalWidthForMacroCustomConfig =
		extension.type === ConfluenceExtension.MACRO &&
		modalExtension?.modalWidth !== undefined &&
		isNamedSize(modalExtension.modalWidth)
			? modalExtension.modalWidth
			: undefined;

	const sizeString = typeof size === 'object' ? undefined : size;
	const modalSize =
		modalWidthForMacroCustomConfig ??
		sizeString ?? // safeguards old logic against custom sizes
		extension.properties.viewportSize ??
		'medium';

	// If size is defined, iframe modal should not be resizable.
	// In JSM ONLY, iframe modal should ALWAYS be resizable.
	// Iframe should scale to 100% of the modal so that Select drop downs are not cut off
	// If developer is using Modal() from bridge and they pick 'resizable' size, the modal will always be resizable.
	const isJsmModule = extension.type.startsWith('jiraServiceManagement');
	const isResizableSize = modalSize === 'resizable';
	const isModalResizable =
		isResizableSize || isJsmModule || (size !== undefined ? false : isIframeResizable);

	const shouldShowFCFModalDeprecationMessage =
		(extension.type === JiraExtension.CUSTOM_FIELD ||
			extension.type === JiraExtension.CUSTOM_FIELD_TYPE) &&
		!fg('forge_disable_fcf_modal_experience_on_issue_view');

	const executeViewOnCloseCallback = async () => {
		if (viewOnClose) {
			try {
				await viewOnClose();
				trackBridgeSucceed({
					method: 'onClose',
					forgeEnvironment: extension.environmentType,
				});
			} catch (error) {
				trackBridgeFailed({
					method: 'onClose',
					errorName: error instanceof Error ? error.name : 'UnknownError',
					forgeEnvironment: extension.environmentType,
				});
			}
		}
	};

	const renderModal = () => {
		const closeProps = {
			onClose: async () => {
				try {
					await executeViewOnCloseCallback();
				} finally {
					modalOnClose?.();
					setIsOpen(false);
				}
			},
			onCloseComplete: onTearDown,
			shouldCloseOnEscapePress: closeOnEscape,
		};

		// Moved up from below as needed for ModalContentWrapper
		const isBridgeModal = !modalExtension;
		const supportsFullscreenModal =
			modalSize === 'fullscreen' &&
			(isBridgeModal || FULLSCREEN_SUPPORTED_EXTENSION_TYPES.has(extension.type));

		const resolvedDimensions = resolveModalDimensions({
			size: modalExtension?.modalWidth ?? size ?? extension.properties.viewportSize,
			getModalWidth,
			shouldFallbackFromFullscreen: modalSize === 'fullscreen' && !supportsFullscreenModal,
			isResizableSize,
		});

		const modalContent = (
			<>
				{shouldShowFCFModalDeprecationMessage && (
					<Box xcss={styles.modalDeprecationMessage}>
						<ForgeModalDeprecationMessage extension={extension} />
					</Box>
				)}
				<ModalContentWrapper
					size={resolvedDimensions.contentSize}
					styles={modalStyles}
					isResizable={isModalResizable}
				>
					<CustomUIRenderer
						product={product}
						coreData={coreData}
						getThreeLOPrompt={getThreeLOPrompt}
						dialogs={dialogs}
						accountId={accountId}
						contextChangeListeners={contextChangeListeners}
						contextIds={contextIds}
						extension={extension}
						extensionData={extensionData}
						extensionViewData={extensionViewData}
						bridge={bridge}
						customBridgeMethods={customBridgeMethods}
						customBridge={customBridge}
						consentMessage={consentMessage}
						entryPoint={entryPoint}
						getContextToken={getContextToken}
						height="100%"
						isIframeHidden={isIframeHidden}
						isIframeResizable={isModalResizable}
						locale={locale}
						localId={localId}
						mentionProvider={mentionProvider}
						modalExtension={modalExtension}
						onConsentSuccess={onConsentSuccess}
						onForgeDocUpdated={onForgeDocUpdated}
						timezone={timezone}
						onConsentModalClose={onConsentModalClose}
						onError={onError}
						onIframeLoad={onIframeLoad}
						loadingComponent={loadingComponent}
						extensionPayload={extensionPayload}
						modalStyles={modalStyles}
						// This is called when `view.close` is called
						onModalCustomUIRendererClose={async () => {
							try {
								await executeViewOnCloseCallback();
							} finally {
								setIsOpen(false);
								// If is a modal extension point, call modalOnClose to reset state from product
								// This is required to reset the state of the modal in the product when view.close is called
								if (modalExtension) {
									modalOnClose?.();
								}
							}
						}}
						isInModal={true}
					/>
				</ModalContentWrapper>
			</>
		);

		// Shared header props for the extracted ModalCustomUIRendererHeader component
		const headerProps: ModalHeaderProps = {
			isBridgeModal,
			isMacroExtension,
			modalSize: resolvedDimensions.contentSize,
			title,
			icon,
			extension,
			modalExtension,
			onClose: closeProps.onClose,
			closeModalExtension: modalExtension?.closeModalExtension,
			executeViewOnCloseCallback,
		};

		if (supportsFullscreenModal) {
			return (
				<FullscreenModal
					closeProps={closeProps}
					isMacroExtension={isMacroExtension}
					isBridgeModal={isBridgeModal}
					modalContent={modalContent}
					headerProps={headerProps}
				/>
			);
		} else {
			// Fallback to 'max' if modalSize is 'fullscreen' but fullscreen is not supported
			const shouldFallbackFromFullscreen = modalSize === 'fullscreen' && !supportsFullscreenModal;
			const resolvedWidth = resolvedDimensions.width;
			const resolvedHeight = resolvedDimensions.height;

			if (shouldFallbackFromFullscreen) {
				// eslint-disable-next-line no-console
				console.warn(
					'Forge fullscreen modal is only supported in the forge/bridge modal and specific extension points. Refer to https://developer.atlassian.com/platform/forge/manifest-reference/modules/ for more guidance. The modal will be rendered with max width instead.',
				);
			}

			return (
				<Modal
					{...closeProps}
					// Width is already 'auto', so no need to check for isResizableSize
					width={resolvedWidth}
					height={resolvedHeight}
					testId="custom-ui-modal-dialog"
					shouldCloseOnOverlayClick={closeOnOverlayClick}
					shouldCloseOnEscapePress={closeOnEscape}
					// Without this prop, the scroll bar sticks outside of the modal with the resizableStyles
					shouldScrollInViewport={isResizableSize}
				>
					{isBridgeModal && title ? <ModalCustomUIRendererHeader {...headerProps} /> : null}
					{modalContent}
				</Modal>
			);
		}
	};

	if (ModalProvider) {
		return (
			<ModalTransition>{isOpen && <ModalProvider>{renderModal()}</ModalProvider>}</ModalTransition>
		);
	}

	return <ModalTransition>{isOpen && renderModal()}</ModalTransition>;
};
